import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  GraduationCap, 
  Globe, 
  Users, 
  Award, 
  CheckCircle,
  ArrowRight,
  Shield
} from 'lucide-react';

const stats = [
  { number: '50+', label: 'Member Institutions' },
  { number: '25+', label: 'Countries' },
  { number: '10K+', label: 'Students Reached' },
  { number: '100+', label: 'Events Organized' },
];

const features = [
  {
    icon: Globe,
    title: 'Global Network',
    description: 'Connect with leading educational institutions across the world and expand your international presence.'
  },
  {
    icon: Award,
    title: 'Official Recognition',
    description: 'Receive internationally recognized certification that validates your institution\'s commitment to excellence.'
  },
  {
    icon: Users,
    title: 'Collaborative Community',
    description: 'Join a vibrant community of educators and administrators working together to advance higher education.'
  },
  {
    icon: Shield,
    title: 'Quality Assurance',
    description: 'Access frameworks and resources that help maintain and improve educational standards.'
  },
];

const membershipBenefits = [
  'Official UIEHEC membership certificate',
  'Access to exclusive events and conferences',
  'Networking with global educational leaders',
  'Participation in joint research initiatives',
  'Visibility in our international directory',
  'Professional development opportunities',
];

export default function HomePage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-[#0E285B]">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1769284005238-9930fafd1395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYnVpbGRpbmclMjBldXJvcGVhbiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzA0MDgyOTl8MA&ixlib=rb-4.1.0&q=85)' }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6 animate-fade-in">
              <img src="/logo-new.png" alt="UIEHEC" className="h-8 w-auto" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                Union of International Education and Higher Education Centers
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in stagger-1">
              Advancing Global Excellence in Higher Education
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-8 animate-fade-in stagger-2">
              Join the Union of International Education and Higher Education Centers (UIEHEC) – 
              a prestigious network of institutions committed to educational excellence across borders.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in stagger-3">
              <Link to="/apply">
                <Button 
                  size="lg" 
                  className="bg-[#F5A623] text-[#0E285B] hover:bg-[#F5A623]/90 h-12 px-8 rounded-sm font-semibold"
                  data-testid="hero-apply-btn"
                >
                  Apply for Membership
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/about">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white/10 h-12 px-8 rounded-sm font-semibold"
                  data-testid="hero-learn-btn"
                >
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0E285B] py-12 border-t border-[#1a3a6e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#F5A623] mb-2">
                  {stat.number}
                </div>
                <div className="text-slate-300 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
              Why Join UIEHEC
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#0E285B] mt-4 mb-4">
              Empowering Educational Excellence
            </h2>
            <div className="w-20 h-1 bg-[#F5A623] mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 group card-hover"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="h-14 w-14 bg-[#0E285B] rounded-sm flex items-center justify-center mb-6 group-hover:bg-[#F5A623] transition-colors duration-300">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                About Us
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#0E285B] mt-4 mb-6">
                A Legacy of Educational Leadership
              </h2>
              <div className="w-20 h-1 bg-[#F5A623] mb-8" />
              <p className="text-slate-600 leading-relaxed mb-6">
                The Union of International Education and Higher Education Centers (UIEHEC) 
                is a prestigious international organization headquartered in Aachen, Germany. 
                We bring together universities, colleges, and educational institutions from 
                around the world.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Our mission is to foster international cooperation, promote academic excellence, 
                and create meaningful partnerships that benefit students and educators globally.
              </p>
              <Link to="/about">
                <Button 
                  className="bg-[#0E285B] text-white hover:bg-[#0E285B]/90 h-11 px-8 rounded-sm"
                  data-testid="about-learn-more-btn"
                >
                  Discover Our Mission
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1769284008835-a7e37a64eca8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYnVpbGRpbmclMjBldXJvcGVhbiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzA0MDgyOTl8MA&ixlib=rb-4.1.0&q=85"
                alt="UIEHEC Headquarters"
                className="rounded-sm shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#F5A623] text-[#0E285B] p-6 rounded-sm">
                <div className="font-['Playfair_Display'] text-2xl font-bold">Since 2020</div>
                <div className="text-sm opacity-90">Serving Global Education</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Benefits */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1741699427799-3fbb70fce948?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwdW5pdmVyc2l0eSUyMHN0dWRlbnRzJTIwc3R1ZHlpbmclMjBsaWJyYXJ5fGVufDB8fHx8MTc3MDQwODMwOXww&ixlib=rb-4.1.0&q=85"
                alt="Students studying"
                className="rounded-sm shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                Membership
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#0E285B] mt-4 mb-6">
                Benefits of UIEHEC Membership
              </h2>
              <div className="w-20 h-1 bg-[#F5A623] mb-8" />
              <ul className="space-y-4 mb-8">
                {membershipBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#F5A623] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link to="/membership">
                <Button 
                  className="bg-[#F5A623] text-[#0E285B] hover:bg-[#F5A623]/90 h-11 px-8 rounded-sm font-semibold"
                  data-testid="membership-learn-btn"
                >
                  View Membership Options
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#0E285B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <GraduationCap className="h-16 w-16 text-[#F5A623] mx-auto mb-6" />
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Join Our Global Network?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Become part of an elite community of educational institutions dedicated to 
            excellence, innovation, and international collaboration.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply">
              <Button 
                size="lg"
                className="bg-[#F5A623] text-[#0E285B] hover:bg-[#F5A623]/90 h-12 px-8 rounded-sm font-semibold"
                data-testid="cta-apply-btn"
              >
                Apply for Membership
              </Button>
            </Link>
            <Link to="/verify">
              <Button 
                variant="outline" 
                size="lg"
                className="border-white text-white hover:bg-white/10 h-12 px-8 rounded-sm"
                data-testid="cta-verify-btn"
              >
                Verify a Certificate
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
